'use client';

import { Card, Label } from '@/components/ui';
import { LazyTabLabECharts } from '@/components/ui/charts/lazy-tab-lab-echarts';
import { ScrollableContainer } from '../common/scrollable-container';
import type { EChartsOption } from 'echarts';

export interface PoissonResult {
  pdf: number;
  cdf: number;
  distribution: number[];
  events: number[];
  currentK: number;
  lambda: number;
}

interface PoissonCalculatorProps {
  visualizationData: PoissonResult | null;
}

export function PoissonCalculator({ visualizationData: result }: PoissonCalculatorProps) {
  if (!result) {
    return null;
  }

  // Custom chart configuration specifically for Poisson distribution
  const chartOption: EChartsOption = {
    title: {
      text: 'Poisson Distribution',
      left: 'center',
      top: 10,
      textStyle: {
        fontSize: 16,
        fontWeight: 'bold' as const
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow'
      },
      formatter: (params: any) => {
        const dataIndex = params[0].dataIndex;
        const xValue = result.events[dataIndex];
        const yValue = result.distribution[dataIndex];
        return `Events: ${xValue}<br/>Probability: ${yValue?.toFixed(4) || 'N/A'}`;
      }
    },
    toolbox: {
      show: true,
      feature: {
        saveAsImage: {
          title: 'Save as Image',
          name: 'poisson-distribution'
        },
        dataZoom: {
          title: {
            zoom: 'Zoom',
            back: 'Zoom Reset'
          }
        }
      }
    },
    grid: {
      top: 60,
      right: 40,
      bottom: 60,
      left: 60,
      containLabel: true
    },
    xAxis: {
      type: 'category',
      name: 'Number of Events',
      nameLocation: 'middle',
      nameGap: 30,
      data: result.events.map(String),
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      }
    },
    yAxis: {
      type: 'value',
      name: 'Probability',
      nameLocation: 'middle',
      nameGap: 40,
      axisLine: {
        show: true
      },
      splitLine: {
        show: true,
        lineStyle: {
          type: 'dashed',
          opacity: 0.3
        }
      }
    },
    series: [
      {
        type: 'bar',
        data: result.distribution,
        itemStyle: {
          color: '#8884d8',
          opacity: 0.8,
          borderRadius: [4, 4, 0, 0]
        },
        emphasis: {
          itemStyle: {
            opacity: 1
          }
        },
        markLine: {
          symbol: ['none', 'none'],
          data: [{
            name: `P(X = ${result.currentK})`,
            xAxis: result.currentK,
            label: {
              formatter: `P(X = ${result.currentK}) = ${typeof result.pdf === 'number' ? result.pdf.toFixed(4) : 'N/A'}`,
              position: 'insideEndTop' as const,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              padding: [4, 8],
              borderRadius: 4
            },
            lineStyle: {
              color: '#ff4d4d',
              type: 'solid',
              width: 2
            }
          }],
          emphasis: {
            lineStyle: {
              width: 3
            }
          }
        }
      }
    ]
  };

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Poisson Distribution Results</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label>Probability Mass (PDF)</Label>
                <div className="text-xl font-medium">{typeof result.pdf === 'number' ? result.pdf.toFixed(4) : 'N/A'}</div>
                <p className="text-sm text-muted-foreground">
                  This is the probability of exactly {result.currentK} event{result.currentK !== 1 ? 's' : ''} occurring 
                  when the average rate is λ = {result.lambda.toFixed(1)}.
                </p>
              </div>
              <div className="space-y-1">
                <Label>Cumulative Probability (CDF)</Label>
                <div className="text-xl font-medium">{typeof result.cdf === 'number' ? result.cdf.toFixed(4) : 'N/A'}</div>
                <p className="text-sm text-muted-foreground">
                  This is the probability of {result.currentK} or fewer event{result.currentK !== 1 ? 's' : ''} occurring
                  (P(X ≤ {result.currentK})).
                </p>
              </div>
            </div>
            <div className="text-sm pt-2 border-t">
              <p className="text-muted-foreground">
                <strong>Interpretation:</strong> With a lambda (average rate) of λ = {result.lambda.toFixed(1)}, there's a {typeof result.pdf === 'number' ? (result.pdf * 100).toFixed(2) : 'N/A'}% chance of 
                observing exactly {result.currentK} event{result.currentK !== 1 ? 's' : ''}, and a {typeof result.cdf === 'number' ? (result.cdf * 100).toFixed(2) : 'N/A'}% chance of observing {result.currentK} or fewer events.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">Probability Distribution</h3>
          <div style={{ height: 300 }}>
            <LazyTabLabECharts option={chartOption} themeName="gentle-photon-stream" />
          </div>
        </Card>
      </div>
    </ScrollableContainer>
  );
} 